import { Link } from '@inertiajs/react'
import { DashboardLayout } from '@/components/layout/DashboardLayout'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { formatCurrency, formatDate } from '@/lib/utils'
import { useBasePath } from '@/lib/paths'
import { DocumentUploader } from '@/components/shared/DocumentUploader'
import {
  FileText, CreditCard, ChevronRight, CalendarDays, Video, Stamp,
  CheckCircle2, ExternalLink, MapPin, Clock
} from 'lucide-react'
import type {
  NotarizationDetailed, NotarizationTypeSummary, NotarizationAct, DocumentAttachment
} from '@/types'

function StageBadge({ stage }: { stage: string }) {
  const config: Record<string, { label: string; className: string }> = {
    application: { label: 'Application', className: 'bg-secondary text-secondary-foreground' },
    payment: { label: 'Paid', className: 'bg-yellow-600 text-white' },
    appointment: { label: 'Appointment', className: 'bg-blue-600 text-white' },
    session: { label: 'Session', className: 'bg-purple-600 text-white' },
    completed: { label: 'Completed', className: 'bg-green-600 text-white' },
  }
  const { label, className } = config[stage] || { label: stage, className: 'bg-secondary text-secondary-foreground' }
  return <Badge className={`font-normal text-xs ${className}`}>{label}</Badge>
}

const STAGE_KEYS = ['application', 'payment', 'appointment', 'session', 'completed'] as const
const STAGE_LABELS: Record<string, string> = {
  application: 'Application',
  payment: 'Payment',
  appointment: 'Appointment',
  session: 'Session',
  completed: 'Completed',
}

function ProgressStepper({ currentStage }: { currentStage: string }) {
  const currentIndex = STAGE_KEYS.indexOf(currentStage as typeof STAGE_KEYS[number])
  return (
    <div className="flex items-center gap-1">
      {STAGE_KEYS.map((key, i) => {
        const done = i <= currentIndex
        const isCurrent = i === currentIndex
        return (
          <div key={key} className="flex-1 flex flex-col gap-2">
            <div className={`h-1.5 rounded-full transition-colors ${done ? 'bg-foreground' : 'bg-muted'}`} />
            <span className={`text-[11px] ${isCurrent ? 'text-foreground font-medium' : done ? 'text-muted-foreground' : 'text-muted-foreground/60'}`}>
              {STAGE_LABELS[key]}
            </span>
          </div>
        )
      })}
    </div>
  )
}

type TypeSummary = {
  id: string; name: string; jurisdiction_code: string; jurisdiction_label: string;
  service_fee_cents: number; gov_fee_cents: number;
  session_modes: string[]; slot_duration_minutes: number;
}

type Props = {
  notarization: NotarizationDetailed
  type: TypeSummary
}

export default function NotarizationShow({ notarization, type }: Props) {
  const basePath = useBasePath()
  const totalCost = type.service_fee_cents + type.gov_fee_cents

  return (
    <DashboardLayout title={notarization.business_name} breadcrumbs={[{ label: 'Notarizations', href: 'notarizations' }, { label: notarization.business_name }]}>
      <div>
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-1">
            <h1 className="text-xl font-semibold tracking-tight">{notarization.business_name}</h1>
            <StageBadge stage={notarization.stage} />
          </div>
          <p className="text-sm text-muted-foreground">
            {type.name} · {type.jurisdiction_label} · {notarization.session_mode === 'ron' ? 'Remote Online' : 'In Person'}
          </p>
          <div className="flex items-center gap-2 mt-3">
            <Button asChild size="sm" variant="outline">
              <Link href={`${basePath}/notarizations/${notarization.id}/documents`}>
                <FileText className="w-3.5 h-3.5 mr-1.5" />
                Documents
                {notarization.documents_count > 0 && (
                  <Badge variant="secondary" className="ml-1.5 font-normal text-xs px-1.5 py-0">{notarization.documents_count}</Badge>
                )}
              </Link>
            </Button>
            <Button asChild size="sm" variant="outline">
              <Link href={`${basePath}/notarizations/${notarization.id}/payment`}>
                <CreditCard className="w-3.5 h-3.5 mr-1.5" />
                Payment
                {notarization.payment?.paid && (
                  <Badge className="ml-1.5 font-normal text-xs px-1.5 py-0 bg-green-600 text-white">Paid</Badge>
                )}
              </Link>
            </Button>
            {notarization.payment?.paid && !notarization.appointment && (
              <Button asChild size="sm" variant="outline">
                <Link href={`${basePath}/notarizations/${notarization.id}/appointments/new`}>
                  <CalendarDays className="w-3.5 h-3.5 mr-1.5" />
                  Book Appointment
                </Link>
              </Button>
            )}
            {notarization.appointment && !notarization.appointment.cancelled && (
              <Button asChild size="sm" variant="outline">
                <Link href={`${basePath}/notarizations/${notarization.id}/appointment`}>
                  <CalendarDays className="w-3.5 h-3.5 mr-1.5" />
                  Appointment
                </Link>
              </Button>
            )}
          </div>
        </div>

        <div className="mb-8 py-4 px-5 border rounded-lg">
          <ProgressStepper currentStage={notarization.stage} />
        </div>

        {/* Get Started callout */}
        {(() => {
          const missing: { label: string; description: string; href: string; icon: React.ReactNode }[] = []
          if (!notarization.payment || !notarization.payment.paid) {
            missing.push({
              label: 'Complete payment',
              description: `Pay ${formatCurrency(totalCost)} to proceed with your notarization`,
              href: `${basePath}/notarizations/${notarization.id}/payment`,
              icon: <CreditCard className="w-4 h-4 text-muted-foreground" />
            })
          }
          if (notarization.payment?.paid && !notarization.appointment) {
            missing.push({
              label: 'Book an appointment',
              description: 'Schedule a session with an available notary',
              href: `${basePath}/notarizations/${notarization.id}/appointments/new`,
              icon: <CalendarDays className="w-4 h-4 text-muted-foreground" />
            })
          }
          if (missing.length === 0) return null
          return (
            <div className="mb-8">
              <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">Get Started</h2>
              <div className="rounded-lg border border-dashed bg-muted/30 divide-y divide-dashed">
                {missing.map(item => (
                  <Link
                    key={item.label}
                    href={item.href}
                    className="flex items-center gap-3 px-4 py-3 hover:bg-muted/50 transition-colors"
                  >
                    <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center shrink-0">
                      {item.icon}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium text-foreground">{item.label}</div>
                      <div className="text-xs text-muted-foreground">{item.description}</div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
                  </Link>
                ))}
              </div>
            </div>
          )
        })()}

        <div className="space-y-6">
          {/* Notarization Details */}
          <section className="border rounded-lg px-4 py-3">
            <p className="text-[12px] font-medium text-muted-foreground uppercase tracking-wider mb-3">Details</p>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Type</span>
                <span className="text-foreground font-medium">{type.name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Business</span>
                <span className="text-foreground">{notarization.business_name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Session Mode</span>
                <span className="text-foreground">{notarization.session_mode === 'ron' ? 'Remote Online (RON)' : 'In Person'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Jurisdiction</span>
                <span className="text-foreground">{type.jurisdiction_label}</span>
              </div>
              {notarization.notes && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Notes</span>
                  <span className="text-foreground text-right max-w-[60%]">{notarization.notes}</span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-muted-foreground">Created</span>
                <span className="text-foreground">{formatDate(notarization.created_at)}</span>
              </div>
            </div>
          </section>

          {/* Payment Section */}
          <section className="border rounded-lg px-4 py-3">
            <p className="text-[12px] font-medium text-muted-foreground uppercase tracking-wider mb-3">Payment</p>
            {notarization.payment?.paid ? (
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Status</span>
                  <span className="flex items-center gap-1 text-green-600 font-medium text-xs">
                    <CheckCircle2 className="w-3 h-3" />
                    Paid on {formatDate(notarization.payment.paid_at!)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Total</span>
                  <span className="text-foreground font-medium">{formatCurrency(notarization.payment.total_cents)}</span>
                </div>
                {notarization.payment.receipt && (
                  <div className="flex justify-between text-xs text-muted-foreground">
                    <span>Receipt</span>
                    <span className="font-mono">{notarization.payment.receipt.number}</span>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-sm text-muted-foreground">
                <p>Payment of {formatCurrency(totalCost)} is required to proceed.</p>
                <Button asChild size="sm" className="mt-2">
                  <Link href={`${basePath}/notarizations/${notarization.id}/payment`}>
                    <CreditCard className="w-3.5 h-3.5 mr-1.5" />
                    Make Payment
                  </Link>
                </Button>
              </div>
            )}
          </section>

          {/* Appointment Section */}
          <section className="border rounded-lg px-4 py-3">
            <p className="text-[12px] font-medium text-muted-foreground uppercase tracking-wider mb-3">Appointment</p>
            {notarization.appointment ? (
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Notary</span>
                  <span className="text-foreground font-medium">{notarization.appointment.notary_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Date & Time</span>
                  <span className="text-foreground">
                    {new Date(notarization.appointment.scheduled_at).toLocaleString('en-US', {
                      month: 'short', day: 'numeric', year: 'numeric',
                      hour: 'numeric', minute: '2-digit'
                    })}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Duration</span>
                  <span className="text-foreground">{notarization.appointment.duration_minutes} minutes</span>
                </div>
                {notarization.appointment.cancelled && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Status</span>
                    <Badge className="font-normal text-xs bg-destructive text-destructive-foreground">Cancelled</Badge>
                  </div>
                )}
                <Button asChild size="sm" variant="outline" className="mt-1">
                  <Link href={`${basePath}/notarizations/${notarization.id}/appointment`}>
                    View Appointment
                  </Link>
                </Button>
              </div>
            ) : notarization.payment?.paid ? (
              <div className="text-sm text-muted-foreground">
                <p>No appointment scheduled yet. Book a session with an available notary.</p>
                <Button asChild size="sm" className="mt-2">
                  <Link href={`${basePath}/notarizations/${notarization.id}/appointments/new`}>
                    <CalendarDays className="w-3.5 h-3.5 mr-1.5" />
                    Book Appointment
                  </Link>
                </Button>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">Complete payment to book an appointment.</p>
            )}
          </section>

          {/* Session Info (RON) */}
          {notarization.session_mode === 'ron' && notarization.session && (
            <section className="border rounded-lg px-4 py-3">
              <p className="text-[12px] font-medium text-muted-foreground uppercase tracking-wider mb-3">Session</p>
              <div className="space-y-2 text-sm">
                {notarization.session.meeting_url && (
                  <div className="flex justify-between items-center">
                    <span className="text-muted-foreground">Meeting Link</span>
                    <a href={notarization.session.meeting_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-foreground font-medium hover:underline">
                      <Video className="w-3.5 h-3.5" />
                      Join Session
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>
                )}
                {notarization.session.started_at && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Started</span>
                    <span className="text-foreground">{new Date(notarization.session.started_at).toLocaleString()}</span>
                  </div>
                )}
                {notarization.session.ended_at && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Ended</span>
                    <span className="text-foreground">{new Date(notarization.session.ended_at).toLocaleString()}</span>
                  </div>
                )}
                {notarization.session.recording_url && (
                  <div className="flex justify-between items-center">
                    <span className="text-muted-foreground">Recording</span>
                    <a href={notarization.session.recording_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-foreground hover:underline text-xs">
                      View Recording
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>
                )}
              </div>
            </section>
          )}

          {/* Documents */}
          <DocumentUploader
            title="Application Documents"
            documents={notarization.application_documents || []}
            uploadPath={`${basePath}/notarizations/${notarization.id}/application_documents`}
            deletePath={(docId) => `${basePath}/notarizations/${notarization.id}/application_documents/${docId}`}
          />

          {/* Notarization Acts */}
          {notarization.acts.length > 0 && (
            <section className="border rounded-lg">
              <div className="px-4 py-3 border-b">
                <h2 className="text-sm font-medium text-foreground">Notarization Acts</h2>
              </div>
              <div className="divide-y">
                {notarization.acts.map(act => (
                  <div key={act.id} className="flex items-center justify-between px-4 py-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-8 h-8 rounded-lg bg-muted flex items-center justify-center shrink-0">
                        <Stamp className="w-4 h-4 text-muted-foreground" />
                      </div>
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-foreground truncate">{act.document_name}</div>
                        <div className="text-xs text-muted-foreground">
                          {act.act_type} · Seal #{act.seal_number} · Cert #{act.certificate_number} · {formatDate(act.notarized_at)}
                        </div>
                        {act.notes && (
                          <div className="text-xs text-muted-foreground mt-0.5">{act.notes}</div>
                        )}
                      </div>
                    </div>
                    {act.certificate_url && (
                      <a href={act.certificate_url} target="_blank" rel="noopener noreferrer">
                        <Button variant="ghost" size="sm" className="text-xs">
                          <ExternalLink className="w-3 h-3 mr-1" />
                          Certificate
                        </Button>
                      </a>
                    )}
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Completion */}
          {notarization.completion && (
            <section className="border rounded-lg px-4 py-3 bg-green-50 dark:bg-green-950/20 border-green-200 dark:border-green-900">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle2 className="w-4 h-4 text-green-600" />
                <p className="text-sm font-medium text-green-800 dark:text-green-400">Notarization Complete</p>
              </div>
              <div className="space-y-1 text-xs text-green-700 dark:text-green-500">
                <p>Completed by {notarization.completion.notary_name} on {formatDate(notarization.completion.created_at)}</p>
              </div>
            </section>
          )}
        </div>
      </div>
    </DashboardLayout>
  )
}
